import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, getCardClass, resolveImage, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

const MONTHS_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function parseDateParts(dateStr) {
    if (!dateStr) return { day: '--', month: '---' };
    const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
    if (isNaN(d.getTime())) return { day: '--', month: '---' };
    return { day: d.getDate(), month: MONTHS_SHORT[d.getMonth()] };
}

const LocationIcon = () => (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
    </svg>
);

const ClockIcon = () => (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
);

const UserIcon = () => (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
    </svg>
);

export default function EventsSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const allItems = (content.items || []).map(item => ({ ...item, ...resolveItemMedia(item, mediaMode, mediaFolders) }));
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'grid';
    const dateBadgeColor = config.date_badge_color || s.accent;
    const columns = config.columns || 3;
    const colClass = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    // Filter past events if configured
    const now = new Date();
    const items = config.hide_past_events
        ? allItems.filter(item => { if (!item.date) return true; const d = new Date(item.date + (item.date.includes('T') ? '' : 'T23:59:59')); return d >= now; })
        : allItems;
    const isPast = (item) => { if (!item.date) return false; const d = new Date(item.date + (item.date.includes('T') ? '' : 'T23:59:59')); return d < now; };

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const BADGE_COLORS = { online: '#10b981', presencial: '#3b82f6', hibrido: '#8b5cf6', agotado: '#ef4444', nuevo: '#f59e0b', gratis: '#06b6d4', ultimo_cupo: '#f97316' };

    const DateBadge = ({ date, past }) => {
        const { day, month } = parseDateParts(date);
        return (
            <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 transition-transform hover:scale-110 ${past ? 'opacity-50 grayscale' : ''}`}
                style={{ backgroundColor: past ? '#9ca3af' : dateBadgeColor, color: '#ffffff' }}>
                <span className="text-xl font-extrabold leading-none">{day}</span>
                <span className="text-[10px] font-semibold uppercase leading-tight">{month}</span>
            </div>
        );
    };

    const StatusBadge = ({ badge }) => {
        if (!badge) return null;
        const color = BADGE_COLORS[badge.toLowerCase().replace(/\s/g, '_')] || s.accent;
        return (
            <span className="inline-block text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide"
                style={{ backgroundColor: hexToRgba(color, 0.12), color }}>
                {badge}
            </span>
        );
    };

    const PriceBadge = ({ price }) => {
        if (!price) return null;
        return (
            <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : hexToRgba(s.accent, 0.08), color: s.dark ? 'rgba(255,255,255,0.9)' : s.accent }}>
                {price}
            </span>
        );
    };

    const CalendarPlaceholder = () => (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ background: `linear-gradient(135deg, ${hexToRgba(dateBadgeColor, 0.15)}, ${hexToRgba(dateBadgeColor, 0.05)})` }}>
            <svg className="w-12 h-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{color: dateBadgeColor}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
        </div>
    );

    // ── List variant (horizontal cards) ──
    if (variant === 'list') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="space-y-4">
                        {items.map((item, i) => {
                            const past = isPast(item);
                            return (
                                <div key={i} className={`${card} overflow-hidden transition-all duration-500 hover:shadow-lg ${past ? 'opacity-60' : ''} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <div className="flex flex-col md:flex-row">
                                        {(item.resolvedImage || item.carouselImages || item.video) && (
                                            <div className="md:w-48 shrink-0 overflow-hidden">
                                                <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/10]" rounded="" />
                                            </div>
                                        )}
                                        <div className="p-5 flex-1 flex items-start gap-4">
                                            <DateBadge date={item.date} past={past} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{item.title}</h3>
                                                    <StatusBadge badge={past ? 'Finalizado' : item.badge} />
                                                </div>
                                                {item.description && <p className="text-sm mb-3 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                                <div className="flex flex-wrap items-center gap-3 text-sm" style={{ color: s.textSub }}>
                                                    {item.location && <span className="flex items-center gap-1"><LocationIcon />{item.location}</span>}
                                                    {item.time && <span className="flex items-center gap-1"><ClockIcon />{item.time}</span>}
                                                    {item.organizer && <span className="flex items-center gap-1"><UserIcon />{item.organizer}</span>}
                                                </div>
                                                <div className="flex items-center gap-3 mt-3">
                                                    <PriceBadge price={item.price} />
                                                    {item.link && !past && (
                                                        <a {...resolveLinkProps(item.link)} className="text-sm font-medium hover:underline" style={{ color: s.accent }}>
                                                            {item.link_text || 'Ver mas'}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant (featured style, bigger cards) ──
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid grid-cols-1 ${colClass} gap-8`}>
                        {items.map((item, i) => {
                            const hasMedia = item.resolvedImage || item.carouselImages || item.video;
                            const past = isPast(item);
                            return (
                                <div key={i} className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-xl ${past ? 'opacity-60' : ''} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                    style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms' }}>
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        {hasMedia ? (
                                            <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/10]" rounded="" />
                                        ) : (
                                            <CalendarPlaceholder />
                                        )}
                                        {hasMedia && (
                                            <div className="absolute top-3 left-3">
                                                <DateBadge date={item.date} past={past} />
                                            </div>
                                        )}
                                        {!hasMedia && (
                                            <div className="absolute top-3 left-3">
                                                <DateBadge date={item.date} past={past} />
                                            </div>
                                        )}
                                        <div className="absolute top-3 right-3 flex items-center gap-2">
                                            {item.badge && !past && <StatusBadge badge={item.badge} />}
                                            {past && <StatusBadge badge="Finalizado" />}
                                            {item.price && (
                                                <span className="bg-white/90 backdrop-blur text-xs font-bold px-3 py-1.5 rounded-full" style={{ color: dateBadgeColor }}>
                                                    {item.price}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-sm mb-3 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                        <div className="space-y-1.5 text-sm" style={{ color: s.textSub }}>
                                            {item.location && <div className="flex items-center gap-2"><LocationIcon /><span>{item.location}</span></div>}
                                            {item.time && <div className="flex items-center gap-2"><ClockIcon /><span>{item.time}</span></div>}
                                            {item.organizer && <div className="flex items-center gap-2"><UserIcon /><span>{item.organizer}</span></div>}
                                        </div>
                                        {item.link && !past && (
                                            <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-4 hover:underline" style={{ color: s.accent }}>
                                                {item.link_text || 'Mas informacion'}
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className={`grid grid-cols-1 ${colClass} gap-6`}>
                    {items.map((item, i) => {
                        const hasMedia = item.resolvedImage || item.carouselImages || item.video;
                        const past = isPast(item);
                        return (
                            <div key={i} className={`${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${past ? 'opacity-60' : ''} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {hasMedia && (
                                    <div className="aspect-[16/10] overflow-hidden relative">
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/10]" rounded="" />
                                        {(item.badge || past) && (
                                            <div className="absolute top-2 right-2"><StatusBadge badge={past ? 'Finalizado' : item.badge} /></div>
                                        )}
                                    </div>
                                )}
                                <div className="p-5">
                                    <div className="flex items-start gap-3 mb-3">
                                        <DateBadge date={item.date} past={past} />
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-base font-bold leading-tight" style={{ color: s.text }}>{item.title}</h3>
                                            {item.location && (
                                                <p className="flex items-center gap-1 text-sm mt-1" style={{ color: s.textSub }}>
                                                    <LocationIcon />{item.location}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {!hasMedia && (item.badge || past) && <div className="mb-2"><StatusBadge badge={past ? 'Finalizado' : item.badge} /></div>}
                                    {item.description && <p className="text-sm mb-3 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                    <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: s.textSub }}>
                                        {item.time && <span className="flex items-center gap-1"><ClockIcon />{item.time}</span>}
                                        {item.organizer && <span className="flex items-center gap-1"><UserIcon />{item.organizer}</span>}
                                    </div>
                                    <div className="flex items-center justify-between mt-3">
                                        <PriceBadge price={item.price} />
                                        {item.link && !past && (
                                            <a {...resolveLinkProps(item.link)} className="text-sm font-medium hover:underline" style={{ color: s.accent }}>
                                                {item.link_text || 'Ver mas'}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
